import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  ExpandMore,
  FileCopyOutlined,
  ReceiptOutlined,
  VerticalAlignBottomOutlined,
} from '@material-ui/icons';
import { ArrowRightIcon } from '@material-ui/pickers/_shared/icons/ArrowRightIcon';
import classNames from 'classnames';
import RegularTable from '../../common/table/RegularTable';
import {
  ALMOST_BLACK_COLOR,
  ALMOST_WHITE_COLOR,
  BACKGROUND_DARK,
  BLACK_COLOR, CELL_LIGHT_COLOR, CUSTOM_GREY_COLOR,
  EXPAND_ROW_COLOR,
  OUTLINE_COLOR_DARK,
} from '../../../const/color-const';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { PAYMENTS_ITEMS_CONFIG, TRANSACTION_ITEMS_CONFIG, TRANSACTION_ROW_CONFIG } from './TransactionConfig';
import { Dialog, DialogContent } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { useDispatch } from 'react-redux';
import { downloadTransaction } from '../../../actions/report-actions';

const [ROW_HEIGHT, DESKTOP_FONT_SIZE, MOBILE_FONT_SIZE] = [30, 14, 12];

const styles = makeStyles(theme => ({
  dialogWrapper: {
    background: theme.palette.type === 'light' ? 'white' : BLACK_COLOR,
  },
  tooltipContainer: {
    width: '100%',
    '& svg': {
      color: 'green',
    },
  },
  tooltipOpen: {
    fontWeight: 'bold',
    '& svg': {
      opacity: 1,
    },
  },
  dialogTitle: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 4, 0, 4),
    fontSize: 16,
    fontWeight: 'bold',
    '& > div': {
      cursor: 'pointer',
    },
    '& svg': {
      color: 'green',
    },
  },
  tooltipTablesWrapper: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingBottom: theme.spacing(4),
    '& thead th': {
      border: theme.palette.type === 'light' ? `1px solid ${ALMOST_WHITE_COLOR}` : '1px solid rgba(255, 255,255, .1)',
    },
    '& thead tr th, tbody tr td': {
      fontSize: DESKTOP_FONT_SIZE,
    },
    '& tr': {
      height: 36,
      
    },
    '& tr td': {
      border: 'none',
    },
    '& > div:first-child thead': {
      border: theme.palette.type === 'light' ? `1px solid ${ALMOST_WHITE_COLOR}` : '1px solid rgba(255, 255,255, .1)',
      background: theme.palette.type === 'light' ? CUSTOM_GREY_COLOR : ALMOST_BLACK_COLOR,
      padding: theme.spacing(1),
      '& th': {
        padding: theme.spacing(1),
        outline: 'none',
        height: 36,
      },
    },
  },
  itemsWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    cursor: 'pointer',
  },
  firstItem: {
    paddingRight: theme.spacing(4),
    paddingLeft: theme.spacing(4),
    textOverflow: 'ellipsis',
    textAlign: 'center',
    overflow: 'hidden',
    cursor: 'pointer',
    outline: 'none',
    width: '100%',
  },
  expandIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    left: 8,
    height: 22,
    '& > svg': {
      opacity: 0.3,
    },
  },
  documentIcon: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    fontSize: MOBILE_FONT_SIZE,
    left: 50,
    '& > svg': {
      height: 18,
      color: 'grey',
    },
  },
  tableTitle: {
    width: '100%',
    textAlign: 'center',
    fontWeight: 'bold',
    borderBottom: 'none',
    padding: theme.spacing(1),
    fontSize: DESKTOP_FONT_SIZE,
    margin: theme.spacing(4, 0, 0, 0),
    color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
    border: theme.palette.type === 'light' ? `1px solid ${ALMOST_WHITE_COLOR}` : '1px solid rgba(255, 255,255, .1)',
    background: theme.palette.type === 'light' ? CUSTOM_GREY_COLOR : ALMOST_BLACK_COLOR,
  },
  tableContainer: {
    boxShadow: 'none',
    borderRadius: 0,
    width: '100%',
    '& table': {
      tableLayout: 'fixed',
    },
    '& thead': {
      background: theme.palette.type === 'light' ? 'white' : BACKGROUND_DARK,
      '& th': {
        color: theme.palette.type === 'light' ? BLACK_COLOR : ALMOST_WHITE_COLOR,
        border: theme.palette.type === 'light' ? `1px solid ${EXPAND_ROW_COLOR}` : `1px solid ${OUTLINE_COLOR_DARK}`,
        outline: 'none',
        padding: '8px !important',
        fontSize: MOBILE_FONT_SIZE,
      },
      '& th:empty': {
        padding: '0px !important',
      },
    },
    '& tbody': {
      '& td': {
        outline: 'none',
        border: theme.palette.type === 'light' ? `1px solid ${EXPAND_ROW_COLOR}` : `1px solid ${OUTLINE_COLOR_DARK}`,
        fontSize: MOBILE_FONT_SIZE,
        height: ROW_HEIGHT,
      },
      '& tr:nth-child(odd)': {
        backgroundColor: theme.palette.type === 'light' ? 'white' : BACKGROUND_DARK,
      },
      '& tr:nth-child(even)': {
        backgroundColor: theme.palette.type === 'light' ? CELL_LIGHT_COLOR : BACKGROUND_DARK,
      },
    },
  },
}));

const TransactionsTooltipContent = ({ params, formValues, openItem, setOpenItem, isOpen, setDownloadProgress }) => {
  const {
    Number,
    CashRegister,
    DateTime,
    EnteredByEmployeeName,
    TotalAmountIncludingVat,
    Type,
    Id,
    IsInvoice,
    InvoiceNumber,
    Items,
    Payments,
  } = params.row;
  const rowNumber = formValues.values.transactionType === 1 ? InvoiceNumber : Number;
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const classes = styles();
  
  const handleDownloadClick = (setDownloadProgress, Number, IsInvoice) => {
    dispatch(downloadTransaction(setDownloadProgress, Number, IsInvoice));
  };
  
  const rowData = [{
    Number: Number,
    CashRegister: CashRegister,
    DateTime: DateTime,
    EnteredByEmployeeName: EnteredByEmployeeName,
    TotalAmountIncludingVat: TotalAmountIncludingVat,
    Type: Type,
  }];
  
  const togglePopup = () => {
    setOpenItem(isOpen(Id) ? openItem.filter(openId => openId !== Id) : [...openItem, Id]);
  };
  
  return (
    <>
      <div className={classNames(classes.tooltipContainer, isOpen(Id) && classes.tooltipOpen)} onClick={togglePopup}>
        <div className={classes.itemsWrapper}>
          <span className={classes.expandIcon}>{isOpen(Id) ? <ExpandMore /> : <ArrowRightIcon />}</span>
          <p className={classes.firstItem}>{rowNumber}</p>
          <span className={classes.documentIcon}>
            {IsInvoice ? <FileCopyOutlined /> : <ReceiptOutlined />}
          </span>
        </div>
      </div>
      <Dialog open={isOpen(Id)} onClose={togglePopup} maxWidth="lg">
        <div className={classes.dialogWrapper}>
          <div className={classes.dialogTitle}>
            <p>{formatMessage({ id: 'report.chart.Transaction', defaultMessage: 'Transaction' })} ID: {Number}</p>
            <Box display={'flex'} alignItems={'center'}
                 onClick={(dispatch) => handleDownloadClick(setDownloadProgress, Id, IsInvoice)}>
              <span><FormattedMessage id="report.toolbar.download" defaultMessage="Download" /> PDF</span>
              <VerticalAlignBottomOutlined />
            </Box>
          </div>
          <DialogContent>
            <div className={classes.tooltipTablesWrapper}>
              <RegularTable
                classes={classes}
                data={rowData}
                tableConfig={TRANSACTION_ROW_CONFIG(formatMessage)}
              />
              <p className={classes.tableTitle}>
                {formatMessage({ id: 'report.transaction.Items', defaultMessage: 'Items' })}
              </p>
              <RegularTable
                classes={classes}
                data={Items}
                tableConfig={TRANSACTION_ITEMS_CONFIG(formatMessage)}
              />
              <p className={classes.tableTitle}>
                {formatMessage({ id: 'report.transaction.Payments', defaultMessage: 'Payments' })}
              </p>
              <RegularTable
                classes={classes}
                data={Payments}
                tableConfig={PAYMENTS_ITEMS_CONFIG(formatMessage)}
              />
            </div>
          </DialogContent>
        </div>
      </Dialog>
    </>
  );
};

export default TransactionsTooltipContent;